import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardHeader, Col, Container, Row, Label, Spinner, Alert, Button, Modal, ModalHeader, ModalBody, Table, Badge, Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import Flatpickr from 'react-flatpickr';
import classnames from 'classnames';
import { APIClient } from '../../../helpers/api_helper';
import { Bar } from 'react-chartjs-2';
import ProcessLogsModal from './ProcessLogsModal';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

interface JobStats {
    status: number;
    count: number;
}

interface ProcessorData {
    processorId: number;
    title: string;
    description: string | null;
    endpointSlug: string;
    stats: JobStats[];
}

interface ApiResponse {
    status: number;
    message: string;
    data: ProcessorData[];
}

interface JobDetail {
    requestId: number;
    processorId: number;
    companyId: number;
    requestTime: string;
    requestIP: string;
    processStatus: number;
    llmId: number;
    llmCost: number;
    llmInputToken: number;
    llmOutputToken: number;
    createdAt: string;
    updatedAt: string;
    elapsedTime: number;
}

interface JobDetailResponse {
    status: number;
    message: string;
    data: JobDetail[];
}

const ProjectJobs = () => {
    const { projectId } = useParams();
    const [activeTab, setActiveTab] = useState<string>('sandbox');
    const [dateRange, setDateRange] = useState<[Date, Date]>(() => {
        const now = new Date();
        const thirtyDaysStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29, 0, 0, 0, 0);
        const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        return [thirtyDaysStart, todayEnd];
    });
    const [selectedDateOption, setSelectedDateOption] = useState({ value: 'last30days', label: 'Last 30 days' });
    const [showCustomPicker, setShowCustomPicker] = useState(false);
    const [processors, setProcessors] = useState<ProcessorData[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    
    // Modal states
    const [showModal, setShowModal] = useState<boolean>(false);
    const [selectedProcessor, setSelectedProcessor] = useState<ProcessorData | null>(null);
    const [jobDetails, setJobDetails] = useState<JobDetail[]>([]);
    const [modalLoading, setModalLoading] = useState<boolean>(false);
    const [modalError, setModalError] = useState<string | null>(null);

    // Process Logs Modal states
    const [showLogsModal, setShowLogsModal] = useState<boolean>(false);
    const [selectedRequestId, setSelectedRequestId] = useState<number | null>(null);

    const apiClient = new APIClient();

    // Determine current environment ID based on active tab
    const currentEnvId = activeTab === 'sandbox' ? 1 : 6;

    // Get environment configuration
    const getEnvironmentConfig = () => {
        if (activeTab === 'sandbox') {
            return { color: 'success', name: 'Sandbox' };
        } else {
            return { color: 'danger', name: 'Production' };
        }
    };

    const toggleTab = (tab: string) => {
        if (activeTab !== tab) {
            setActiveTab(tab);
        }
    };

    const getStatusLabel = (status: number): string => {
        switch (status) {
            case 1: return 'New';
            case 4: return 'Failed';
            case 6: return 'Success';
            default: return `Status ${status}`;
        }
    };

    const getStatusColor = (status: number): string => {
        switch (status) {
            case 1: return 'info';
            case 4: return 'danger';
            case 6: return 'success';
            default: return 'secondary';
        }
    };

    const fetchJobData = async () => {
        if (!projectId) return;
        
        setLoading(true);
        setError(null);
        
        try {
            const startTime = formatDateForAPI(dateRange[0]);
            const endTime = formatDateForAPI(dateRange[1]);
            
            const response = await apiClient.get(
                `/job/${projectId}/${currentEnvId}?start=${encodeURIComponent(startTime)}&end=${encodeURIComponent(endTime)}`
            );
            // The APIClient interceptor returns response.data directly
            const apiResponse = response as unknown as ApiResponse;
            
            if (apiResponse.status === 200) {
                setProcessors(apiResponse.data);
            } else {
                setError(apiResponse.message || 'Failed to fetch job data');
            }
        } catch (err) {
            setError('An error occurred while fetching job data');
            console.error('Error fetching job data:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchJobData();
    }, [projectId, activeTab, dateRange]); // eslint-disable-line react-hooks/exhaustive-deps

    const dateOptions = [
        { value: 'today', label: 'Today' },
        { value: 'yesterday', label: 'Yesterday' },
        { value: 'last7days', label: 'Last 7 days' },
        { value: 'last30days', label: 'Last 30 days' },
        { value: 'thismonth', label: 'This month' },
        { value: 'custom', label: 'Custom' }
    ];

    const getDateRangeFromOption = (option: string): [Date, Date] => {
        const now = new Date();
        
        switch (option) {
            case 'today':
                const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
                const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
                return [todayStart, todayEnd];
                
            case 'yesterday':
                const yesterdayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 0, 0, 0, 0);
                const yesterdayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59, 999);
                return [yesterdayStart, yesterdayEnd];
                
            case 'last7days':
                const sevenDaysStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6, 0, 0, 0, 0);
                const sevenDaysEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
                return [sevenDaysStart, sevenDaysEnd];
                
            case 'last30days':
                const thirtyDaysStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29, 0, 0, 0, 0);
                const thirtyDaysEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
                return [thirtyDaysStart, thirtyDaysEnd];
                
            case 'thismonth':
                const monthStart = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
                const monthEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
                return [monthStart, monthEnd];
                
            default:
                return [new Date(dateRange[0]), new Date(dateRange[1])];
        }
    };

    const handleDateOptionChange = (selectedOption: any) => {
        setSelectedDateOption(selectedOption);
        setLoading(true);
        
        if (selectedOption.value === 'custom') {
            setShowCustomPicker(true);
        } else {
            setShowCustomPicker(false);
            const newRange = getDateRangeFromOption(selectedOption.value);
            setDateRange(newRange);
        }
    };

    const handleCustomDateChange = (selectedDates: Date[]) => {
        if (selectedDates.length === 2) {
            setLoading(true);
            setDateRange([new Date(selectedDates[0]), new Date(selectedDates[1])]);
        }
    };

    const formatDateForAPI = (date: Date): string => {
        return date.toISOString().slice(0, 19).replace('T', ' ');
    };

    const fetchJobDetails = async (processorId: number) => {
        if (!projectId) return;
        
        setModalLoading(true);
        setModalError(null);
        
        try {
            const startTime = formatDateForAPI(dateRange[0]);
            const endTime = formatDateForAPI(dateRange[1]);
            
            const response = await apiClient.get(
                `/job/${projectId}/${currentEnvId}/${processorId}?start=${encodeURIComponent(startTime)}&end=${encodeURIComponent(endTime)}`
            );
            const apiResponse = response as unknown as JobDetailResponse;
            
            if (apiResponse.status === 200) {
                setJobDetails(apiResponse.data);
            } else {
                setModalError(apiResponse.message || 'Failed to fetch job details');
            }
        } catch (err) {
            setModalError('An error occurred while fetching job details');
            console.error('Error fetching job details:', err);
        } finally {
            setModalLoading(false);
        }
    };

    const handleViewDetails = (processor: ProcessorData) => {
        setSelectedProcessor(processor);
        setShowModal(true);
        fetchJobDetails(processor.processorId);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedProcessor(null);
        setJobDetails([]);
        setModalError(null);
    };

    const handleViewLogs = (requestId: number) => {
        setSelectedRequestId(requestId);
        setShowLogsModal(true);
    };

    const handleCloseLogsModal = () => {
        setShowLogsModal(false);
        setSelectedRequestId(null);
    };

    const prepareChartData = () => {
        if (!jobDetails.length) return { labels: [], datasets: [] };

        // Group data by date
        const dateGroups: { [key: string]: { failed: number; success: number } } = {};
        
        jobDetails.forEach(job => {
            const date = job.requestTime.split(' ')[0]; // Extract date part
            
            if (!dateGroups[date]) {
                dateGroups[date] = { failed: 0, success: 0 };
            }
            
            if (job.processStatus === 4) {
                dateGroups[date].failed++;
            } else if (job.processStatus === 6) {
                dateGroups[date].success++;
            }
        });

        const sortedDates = Object.keys(dateGroups).sort();
        const failedData = sortedDates.map(date => dateGroups[date].failed);
        const successData = sortedDates.map(date => dateGroups[date].success);

        return {
            labels: sortedDates,
            datasets: [
                {
                    label: 'Failed',
                    data: failedData,
                    backgroundColor: 'rgba(220, 53, 69, 0.8)',
                    borderColor: 'rgba(220, 53, 69, 1)',
                    borderWidth: 1,
                },
                {
                    label: 'Success',
                    data: successData,
                    backgroundColor: 'rgba(25, 135, 84, 0.8)',
                    borderColor: 'rgba(25, 135, 84, 1)',
                    borderWidth: 1,
                },
            ],
        };
    };

    const chartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top' as const,
            },
            title: {
                display: true,
                text: 'Job Status by Date',
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: 1,
                },
            },
        },
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Jobs" pageTitle="Projects" />

                    {/* Environment Tabs */}
                    <Row className="mb-3">
                        <Col xl={12}>
                            <Nav tabs className="nav-tabs-custom nav-success">
                                <NavItem>
                                    <NavLink
                                        style={{ cursor: 'pointer' }}
                                        className={classnames({ active: activeTab === 'sandbox' })}
                                        onClick={() => toggleTab('sandbox')}
                                    >
                                        <i className="ri-flask-line me-1 align-bottom"></i>
                                        Sandbox
                                    </NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink
                                        style={{ cursor: 'pointer' }}
                                        className={classnames({ active: activeTab === 'production' })}
                                        onClick={() => toggleTab('production')}
                                    >
                                        <i className="ri-rocket-line me-1 align-bottom"></i>
                                        Production
                                    </NavLink>
                                </NavItem>
                            </Nav>
                        </Col>
                    </Row>

                    {/* Date Range Filter */}
                    <Row className="mb-3">
                        <Col lg={12}>
                            <div className="d-flex justify-content-end">
                                <div className="d-flex align-items-center">
                                    <Label className="me-2 mb-0">Date Range:</Label>
                                    <div style={{ width: '200px', marginRight: '10px' }}>
                                        <Select
                                            value={selectedDateOption}
                                            onChange={handleDateOptionChange}
                                            options={dateOptions}
                                            isSearchable={false}
                                            placeholder="Select date range"
                                        />
                                    </div>
                                    {showCustomPicker && (
                                        <div style={{ width: '250px' }}>
                                            <Flatpickr
                                                className="form-control"
                                                options={{
                                                    mode: 'range',
                                                    dateFormat: 'Y-m-d',
                                                    defaultDate: dateRange,
                                                }}
                                                onChange={handleCustomDateChange}
                                                placeholder="Select custom date range"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Loading State */}
                    {loading && (
                        <Row>
                            <Col lg={12}>
                                <div className="text-center p-4">
                                    <Spinner color="primary" />
                                    <p className="mt-2 text-muted">Loading job data...</p>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* Error State */}
                    {error && (
                        <Row>
                            <Col lg={12}>
                                <Alert color="danger">
                                    <i className="mdi mdi-alert-outline me-2"></i>
                                    {error}
                                </Alert>
                            </Col>
                        </Row>
                    )}

                    {/* Processor Data Panels */}
                    {!loading && !error && (
                        <Row>
                            {processors.length === 0 ? (
                                <Col lg={12}>
                                    <Card>
                                        <CardBody>
                                            <div className="text-center">
                                                <i className="mdi mdi-information-outline display-4 text-muted"></i>
                                                <h5 className="mt-3">No Job Data Found</h5>
                                                <p className="text-muted">No processors found for the selected date range.</p>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                            ) : (
                                processors.map((processor) => (
                                    <Col lg={6} xl={4} key={processor.processorId} className="mb-3">
                                        <Card className={`h-100 card-animate border-${getEnvironmentConfig().color}`}>
                                            <CardHeader>
                                                <div className="d-flex align-items-center">
                                                    <div className="flex-grow-1">
                                                        <h5 className="card-title mb-0">{processor.title}</h5>
                                                        {processor.description && (
                                                            <p className="text-muted mb-0 small">{processor.description}</p>
                                                        )}
                                                    </div>
                                                    <div className="flex-shrink-0">
                                                        <span className="badge badge-soft-info">ID: {processor.processorId}</span>
                                                    </div>
                                                </div>
                                            </CardHeader>
                                            <CardBody>
                                                <div className="mb-3">
                                                    <Label className="form-label fw-semibold">Endpoint:</Label>
                                                    <p className="mb-3">
                                                        <code>{processor.endpointSlug}</code>
                                                    </p>
                                                </div>
                                                
                                                <div>
                                                    <Label className="form-label fw-semibold">Status Summary:</Label>
                                                    <div className="row g-2">
                                                        {processor.stats.map((stat) => {
                                                            const statusColor = getStatusColor(stat.status);
                                                            const isFailedOrSuccess = stat.status === 4 || stat.status === 6;
                                                            
                                                            return (
                                                                <div key={stat.status} className="col-6">
                                                                    <div className={`p-2 border rounded text-center ${isFailedOrSuccess ? `bg-${statusColor}` : `bg-soft-${statusColor}`}`}>
                                                                        <div className={`${isFailedOrSuccess ? 'text-white' : `text-${statusColor}`} fw-semibold`}>
                                                                            {stat.count}
                                                                        </div>
                                                                        <div className={`small ${isFailedOrSuccess ? 'text-white opacity-75' : 'text-muted'}`}>
                                                                            {getStatusLabel(stat.status)}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                    
                                                    {/* Total Count */}
                                                    <div className="mt-2 pt-2 border-top">
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <div>
                                                                <span className="fw-semibold">Total Jobs:</span>
                                                                <span className="fw-semibold text-primary ms-2">
                                                                    {processor.stats.reduce((total, stat) => total + stat.count, 0)}
                                                                </span>
                                                            </div>
                                                            <Button
                                                                color="primary"
                                                                size="sm"
                                                                onClick={() => handleViewDetails(processor)}
                                                            >
                                                                <i className="mdi mdi-eye me-1"></i>
                                                                View Details
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                ))
                            )}
                        </Row>
                    )}

                    {/* Job Details Modal */}
                    <Modal isOpen={showModal} toggle={handleCloseModal} size="xl">
                        <ModalHeader toggle={handleCloseModal}>
                            Job Details - {selectedProcessor?.title}
                            <small className="text-muted ms-2">
                                ({selectedProcessor?.endpointSlug})
                            </small>
                        </ModalHeader>
                        <ModalBody>
                            {modalLoading && (
                                <div className="text-center p-4">
                                    <Spinner color="primary" />
                                    <p className="mt-2 text-muted">Loading job details...</p>
                                </div>
                            )}

                            {modalError && (
                                <Alert color="danger">
                                    <i className="mdi mdi-alert-outline me-2"></i>
                                    {modalError}
                                </Alert>
                            )}

                            {!modalLoading && !modalError && jobDetails.length > 0 && (
                                <>
                                    {/* Bar Chart */}
                                    <div className="mb-4">
                                        <h5 className="mb-3">Job Status Distribution by Date</h5>
                                        <div style={{ height: '400px' }}>
                                            <Bar data={prepareChartData()} options={chartOptions} />
                                        </div>
                                    </div>

                                    {/* Data Table */}
                                    <div>
                                        <h5 className="mb-3">Job Details ({jobDetails.length} records)</h5>
                                        <div className="table-responsive">
                                            <Table className="table-nowrap mb-0">
                                                <thead>
                                                    <tr>
                                                        <th>Request Time</th>
                                                        <th>Request IP</th>
                                                        <th>Created At</th>
                                                        <th>Elapsed Time (s)</th>
                                                        <th>Status</th>
                                                        <th>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {jobDetails.map((job) => (
                                                        <tr key={job.requestId}>
                                                            <td>{job.requestTime}</td>
                                                            <td>{job.requestIP}</td>
                                                            <td>{job.createdAt}</td>
                                                            <td>{job.elapsedTime.toFixed(2)}</td>
                                                            <td>
                                                                <Badge
                                                                    color={getStatusColor(job.processStatus)}
                                                                    className="badge-soft"
                                                                >
                                                                    {getStatusLabel(job.processStatus)}
                                                                </Badge>
                                                            </td>
                                                            <td>
                                                                <Button
                                                                    color="info"
                                                                    size="sm"
                                                                    outline
                                                                    onClick={() => handleViewLogs(job.requestId)}
                                                                >
                                                                    <i className="mdi mdi-file-document-outline me-1"></i>
                                                                    View Logs
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </div>
                                </>
                            )}

                            {!modalLoading && !modalError && jobDetails.length === 0 && (
                                <div className="text-center p-4">
                                    <i className="mdi mdi-information-outline display-4 text-muted"></i>
                                    <h5 className="mt-3">No Job Details Found</h5>
                                    <p className="text-muted">No job details found for the selected date range.</p>
                                </div>
                            )}
                        </ModalBody>
                    </Modal>

                    {/* Process Logs Modal */}
                    <ProcessLogsModal
                        isOpen={showLogsModal}
                        toggle={handleCloseLogsModal}
                        requestId={selectedRequestId}
                    />
                </Container>
            </div>
        </React.Fragment>
    );
};

export default ProjectJobs;